import { useCallback, useMemo } from 'react';

import { jumpCompany } from 'app/api/admin';
import { clearSessionCurrencyTypeId } from 'app/api/cookie';
import { useUpdateSession } from 'app/api/hooks/core/v1/auth/user';

interface JumpCompanyGuidArgs {
  companyId: string;
  onError?: () => void;
  onSuccess?: () => void;
}

interface ExternalUserJumpCompanyProps {
  jumpToCompany: (args: JumpCompanyGuidArgs) => void;
}

interface JumpCompanyIdArgs {
  companyId: number;
  onError?: () => void;
  onSuccess?: () => void;
}

interface InternalUserJumpCompanyProps {
  jumpToCompany: (args: JumpCompanyIdArgs) => Promise<void>;
}

// This is a stop gap to get FLS's jumping behavior back to the way they want it while we decide the best path forward.
// They do NOT want to go to the dashboard each time the jump and rely on staying on the same page.
// A longer term solution is needed and if this remains for long, somebody dropped the ball and this needs
// to be looked into.
const legacyJumpBehaviorCompanyIds = new Set([
  // FLS/Scott Logistics/Anderson Transportation - all related to each other.
  // select c.name, c.CompanyGuid
  // from company c
  // inner join Payor p on c.CompanyId = p.CompanyId
  // inner join PayorIntegration pi on p.CompanyId = pi.PayorCompanyId
  // where (c.name like '%FLS%' OR c.name like 'SCOTT logist%' or c.name like '%anderson%') and c.CompanyTypeId = 1
  '23f14391-2366-4463-805a-485d793489d5',
  'c7dfbbf7-b071-49ba-8c03-0646bacd3950',
  '8340fca1-d894-467e-9a00-e8d0d3c6dba3',
  '3828fc40-6f5f-4976-9995-e4ae34e07933',
  'af1a22cc-0d33-4779-9e06-359f310b6349',
  'deedfd31-fdd6-4f46-9ec2-09baa364e078',
  'a601b986-6818-4961-b67e-71e0e7a8c58b',
  '94dd67e2-1dbd-4c0a-9b1c-4f310248fb25',

  // local dev for testing
  // KLM Enterprise and Liberty Lane LLC
  '93db7782-bfa1-4797-811e-be34feba3442',
  '7c78b257-5b6e-49a0-81ff-04f779989da8',
].map(x => x.toLowerCase()));

export const useExternalUserCompanyJumper = (): ExternalUserJumpCompanyProps => {
  const {
    mutate: updateSession,
  } = useUpdateSession();

  const jumpToCompany = useCallback(
    ({
      companyId,
      onError,
      onSuccess,
    }: JumpCompanyGuidArgs): void => {
      updateSession({ companyId },
        {
          onError,
          onSuccess: () => {
            onSuccess?.();
            clearSessionCurrencyTypeId();

            if (legacyJumpBehaviorCompanyIds.has(companyId.toLowerCase())) {
              // this is a hack while we figure out how to satisfy FLS's desire to remain on the same page when jumping
              // vs Bridgeway's (and others) issues where they get 404s when jumping.
              window.location.reload();
            } else {
              // redirect to the home page AND do a page reload
              // The home page aspect is important to avoid 404s when trying to access one company's resource from
              // another. For example if you're at /invoices/123 which is owned by company 1, jumping to company 2
              // and accessing /invoices/123 will result in a 404.
              window.location.href = window.location.origin;
            }
          },
        });
    },
    [updateSession],
  );

  return useMemo(
    () => ({
      jumpToCompany,
    }),
    [jumpToCompany],
  );
};

export const useInternalUserCompanyJumper = (): InternalUserJumpCompanyProps => {
  return useMemo(
    () => ({
      jumpToCompany: async ({
        companyId,
        onError,
        onSuccess,
      }: JumpCompanyIdArgs): Promise<void> => {
        try {
          await jumpCompany({ companyId });
          onSuccess?.();
        } catch (_e) {
          onError?.();
        }
      },
    }),
    [],
  );
};
